/*
 * Tiered ping-tree builder logic (CU-LS-M1-057). Pure functions, no React.
 *
 * The order here must match lds-api WaterfallPlanner.ranked exactly:
 *   Exit offers follow every posted buyer (the API export supplies the role hint).
 *   1. tier ascending; a campaign with no tier goes after every tiered campaign
 *   2. buyer price descending: the campaign's fixed price, else the lead's price (none = last)
 *   3. priority descending (none counts as 0)
 *   4. uniqueId ascending, then campaign id
 * A tree with manualOrder = true is tried in its list order instead.
 *
 * tier and priority are stored on the tree slot, so they apply to this tree only (CU-LS-M1-071). price is stored on
 * the campaign: a change applies to every tree that lists it. The API import (POST /tree/import) is the only write
 * path used by the builder.
 */

export const TREE_FORMAT = "lds-ping-tree/v1";
export const MAX_TIER = 1000;
export const MAX_PRIORITY = 1000;
export const MAX_PRICE = 10000;
export const MAX_CAMPAIGNS = 1000;

export const TREE_SETTING_FIELDS = ["tierOrder", "tailBudgetMs", "tailCampaignCount"];
const CAMPAIGN_FIELDS = ["tier", "price", "priority", "product", "weight"];
const hasOwn = (object, key) => Object.prototype.hasOwnProperty.call(object || {}, key);
const presentFields = (object, fields) => Object.fromEntries(fields.filter((f) => hasOwn(object, f)).map((f) => [f, object[f]]));

const isBlank = (v) => v === null || v === undefined || v === "";

// Java BigDecimal string syntax, which the API (CampaignRankInput) parses: "60", "+.50", "1e2", "0.2900".
const DECIMAL = /^[+-]?(\d+\.?\d*|\.\d+)([eE][+-]?\d+)?$/;

/** Number from a JSON number or numeric string; null for blank; NaN for anything else. */
export const toNumber = (v) => {
  if (isBlank(v)) return null;
  if (typeof v === "number") return v;
  if (typeof v === "string" && DECIMAL.test(v.trim())) return Number(v.trim());
  return NaN;
};

/**
 * Decimal places after removing trailing zeros, computed on the text like BigDecimal.stripTrailingZeros().scale()
 * (negative = a whole number with trailing zeros). JSON numbers are checked as the double the API receives.
 */
const strippedScale = (value) => {
  let text = typeof value === "number" ? String(value) : String(value).trim();
  let exp = 0;
  const e = text.search(/[eE]/);
  if (e >= 0) {
    exp = parseInt(text.slice(e + 1), 10);
    text = text.slice(0, e);
  }
  text = text.replace(/^[+-]/, "");
  const [intPart, frac = ""] = text.split(".");
  let digits = (intPart + frac).replace(/^0+/, "");
  let scale = frac.length - exp;
  if (digits === "") return 0;
  while (digits.endsWith("0")) {
    digits = digits.slice(0, -1);
    scale -= 1;
  }
  return scale;
};

/** Error message for a tier/price/priority value (same rules and wording as the API), or null when valid. */
export const fieldError = (field, value) => {
  const n = toNumber(value);
  if (n === null) return null;
  if (Number.isNaN(n)) return `${field} must be a number`;
  if (field === "price") {
    if (n <= 0 || n > MAX_PRICE) return `price must be greater than 0 and at most ${MAX_PRICE}`;
    if (strippedScale(value) > 2) return "price can have at most 2 decimal places";
    return null;
  }
  if (strippedScale(value) > 0) return `${field} must be a whole number`;
  const [min, max] = field === "tier" ? [1, MAX_TIER] : [0, MAX_PRIORITY];
  if (n < min || n > max) return `${field} must be between ${min} and ${max}`;
  return null;
};

/** The value as sent to the API: numbers as numbers, typed text unchanged (the API validates it exactly). */
const toWire = (v) => (isBlank(v) ? null : typeof v === "string" ? v.trim() : v);

/** A campaign slot in the editor, from an export entry or a get-all-campaign row. */
export const toSlot = (row) => ({
  campaignId: row?.campaignId ?? row?.id ?? null,
  uniqueId: row?.uniqueId ?? null,
  campaignName: row?.campaignName ?? "",
  buyerId: row?.buyerId ?? null,
  buyerName: row?.buyerName ?? "",
  campaignType: row?.campaignType ?? "",
  active: row?.active ?? row?.status ?? true,
  capped: row?.capped === true,
  tier: isBlank(row?.tier) ? null : row.tier,
  price: isBlank(row?.price) ? null : row.price,
  priority: isBlank(row?.priority) ? null : row.priority,
  ...presentFields(row, ["product", "weight", "position", "exitOffer"]),
  alsoInTrees: Array.isArray(row?.alsoInTrees) ? row.alsoInTrees : [],
});

const num = (v) => {
  const n = toNumber(v);
  return n === null || Number.isNaN(n) ? null : n;
};

/** Price the buyer is posted for a lead at leadPrice (null leadPrice = unknown). */
export const effectivePrice = (slot, leadPrice = null) => {
  const p = num(slot.price);
  return p !== null ? p : num(leadPrice);
};

const nullsLastAsc = (a, b) => {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return a - b;
};

const nullsLastDesc = (a, b) => {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return b - a;
};

/** Comparator matching WaterfallPlanner.ranked. */
const compareRole = (a, b) => Number(a.exitOffer === true) - Number(b.exitOffer === true);

export const compareRanked = (leadPrice = null) => (a, b) =>
  compareRole(a, b) ||
  nullsLastAsc(num(a.tier), num(b.tier)) ||
  nullsLastDesc(effectivePrice(a, leadPrice), effectivePrice(b, leadPrice)) ||
  (num(b.priority) ?? 0) - (num(a.priority) ?? 0) ||
  (Number(a.uniqueId) || 0) - (Number(b.uniqueId) || 0) ||
  String(a.campaignId ?? "").localeCompare(String(b.campaignId ?? ""));

/** The order the API tries the campaigns in. Paused campaigns are included (the caller marks them skipped). */
export const walkOrder = (slots, { manualOrder = false, leadPrice = null } = {}) =>
  [...slots].sort(manualOrder ? compareRole : compareRanked(leadPrice));

export const formatMoney = (v) => {
  const n = num(v);
  if (n === null) return "lead price";
  return `$${Number.isInteger(n) ? n : n.toFixed(2)}`;
};

/** "$100", "$55–$60", "lead price" or "$20 + lead price" for the prices in one tier. */
export const tierPriceLabel = (campaigns) => {
  const prices = campaigns.map((c) => num(c.price)).filter((p) => p !== null);
  const hasOpen = campaigns.some((c) => num(c.price) === null);
  if (prices.length === 0) return hasOpen ? "lead price" : "";
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  const range = min === max ? formatMoney(min) : `${formatMoney(min)}–${formatMoney(max)}`;
  return hasOpen ? `${range} + lead price` : range;
};

/** A manual-order tree is one list; its order is the walk order. */
export const MANUAL_GROUP = "manual";

/**
 * Moves a campaign inside a manual-order tree's list: "up", "down", "top" or "bottom". The list order is what the
 * API saves as campaignIds and tries in that order.
 */
export const moveInList = (slots, campaignId, where) => {
  const slot = slots.find((s) => s.campaignId === campaignId);
  if (!slot) return { slots, error: "Campaign is not in this tree" };
  const sameRole = (s) => (s.exitOffer === true) === (slot.exitOffer === true);
  const roleSlots = slots.filter(sameRole);
  const i = roleSlots.findIndex((s) => s.campaignId === campaignId);
  const j = where === "up" ? i - 1 : where === "down" ? i + 1 : where === "top" ? 0 : roleSlots.length - 1;
  if (j < 0 || j >= roleSlots.length || j === i) {
    return { slots, error: `Already ${where === "up" || where === "top" ? "first" : "last"} in this role's list` };
  }
  const [moved] = roleSlots.splice(i, 1);
  roleSlots.splice(j, 0, moved);
  let next = 0;
  return { slots: slots.map((s) => sameRole(s) ? roleSlots[next++] : s).sort(compareRole), error: null };
};

/**
 * Groups in walk order. Ranked trees: [{tier, label, campaigns}] with untiered (tier null) last. Manual-order trees:
 * one group (tier MANUAL_GROUP) in list order.
 */
export const groupTree = (slots, { manualOrder = false, leadPrice = null } = {}) =>
  manualOrder
    ? slots.length
      ? [{ tier: MANUAL_GROUP, label: "", campaigns: walkOrder(slots, { manualOrder: true }) }]
      : []
    : groupByTier(slots, leadPrice);

/** Tier groups in walk order: [{tier, label, campaigns}] with untiered (tier null) last. */
export const groupByTier = (slots, leadPrice = null) => {
  const groups = [];
  walkOrder(slots, { leadPrice }).forEach((slot) => {
    const tier = num(slot.tier);
    let group = groups.find((g) => g.tier === tier && !!g.exitOffer === (slot.exitOffer === true));
    if (!group) {
      group = { tier, campaigns: [], ...(slot.exitOffer === true ? { exitOffer: true } : {}) };
      groups.push(group);
    }
    group.campaigns.push(slot);
  });
  return groups.map((g) => ({ ...g, label: tierPriceLabel(g.campaigns) }));
};

/** The slot with a new tier, which is then always sent (a file-omitted tier stops being omitted). */
const withTier = (s, tier) => ({ ...s, tier, ...(s.omit ? { omit: s.omit.filter((f) => f !== "tier") } : {}) });

const replaceSlots = (slots, updates) =>
  slots.map((s) => {
    const u = updates[s.campaignId];
    if (!u) return s;
    const omit = (s.omit || []).filter((f) => !Object.prototype.hasOwnProperty.call(u, f));
    return { ...s, ...u, ...(s.omit ? { omit } : {}) };
  });

/** New priorities for a run of campaigns (same tier and price) so they are tried in the given order. */
const renumberRun = (run) => {
  const n = run.length;
  if (n - 1 > MAX_PRIORITY) return null;
  const step = (n - 1) * 10 <= MAX_PRIORITY ? 10 : 1;
  const updates = {};
  run.forEach((s, i) => {
    updates[s.campaignId] = { priority: (n - 1 - i) * step };
  });
  return updates;
};

const sameTierAndPrice = (a, b, leadPrice) =>
  (a.exitOffer === true) === (b.exitOffer === true) && num(a.tier) === num(b.tier) && effectivePrice(a, leadPrice) === effectivePrice(b, leadPrice);

/** Campaigns in the same tier and at the same price as slot, in walk order. */
const runOf = (slots, slot, leadPrice) =>
  walkOrder(slots, { leadPrice }).filter((s) => sameTierAndPrice(s, slot, leadPrice));

const priceDecides = (a, b, leadPrice) =>
  `Price decides the order inside a tier: ${formatMoney(effectivePrice(a, leadPrice))} is always tried ` +
  `before ${formatMoney(effectivePrice(b, leadPrice))}. Change the price or move the campaign to another tier.`;

/**
 * Moves a campaign one place up or down inside its tier by changing priorities. Only possible next to a campaign
 * at the same price; returns {slots, error}.
 */
export const moveWithinTier = (slots, campaignId, direction, leadPrice = null) => {
  const selected = slots.find((s) => s.campaignId === campaignId);
  const ordered = walkOrder(slots.filter((s) => (s.exitOffer === true) === (selected?.exitOffer === true)), { leadPrice });
  const index = ordered.findIndex((s) => s.campaignId === campaignId);
  if (index < 0) return { slots, error: "Campaign is not in this tree" };
  const slot = ordered[index];
  const neighbour = ordered[direction === "up" ? index - 1 : index + 1];
  if (!neighbour || num(neighbour.tier) !== num(slot.tier)) {
    return { slots, error: `Already ${direction === "up" ? "first" : "last"} in its tier` };
  }
  if (!sameTierAndPrice(slot, neighbour, leadPrice)) {
    return {
      slots,
      error: direction === "up" ? priceDecides(neighbour, slot, leadPrice) : priceDecides(slot, neighbour, leadPrice),
    };
  }
  const run = runOf(slots, slot, leadPrice);
  const i = run.findIndex((s) => s.campaignId === campaignId);
  const j = direction === "up" ? i - 1 : i + 1;
  const reordered = [...run];
  [reordered[i], reordered[j]] = [reordered[j], reordered[i]];
  const updates = renumberRun(reordered);
  if (!updates) return { slots, error: "Too many campaigns at one price in this tier" };
  return { slots: replaceSlots(slots, updates), error: null };
};

/**
 * Moves a campaign to the top or bottom of its tier. Price still decides first, so it moves to the edge of the
 * campaigns at its own price; {note} explains when higher/lower-priced campaigns stay ahead/behind.
 */
export const moveToTierEdge = (slots, campaignId, edge, leadPrice = null) => {
  const slot = slots.find((s) => s.campaignId === campaignId);
  if (!slot) return { slots, error: "Campaign is not in this tree" };
  const run = runOf(slots, slot, leadPrice).filter((s) => s.campaignId !== campaignId);
  const reordered = edge === "top" ? [slot, ...run] : [...run, slot];
  const updates = renumberRun(reordered);
  if (!updates) return { slots, error: "Too many campaigns at one price in this tier" };
  const tierMates = slots.filter((s) => (s.exitOffer === true) === (slot.exitOffer === true) && num(s.tier) === num(slot.tier) && !sameTierAndPrice(s, slot, leadPrice));
  const price = effectivePrice(slot, leadPrice);
  const blocking = tierMates.filter((s) => {
    const p = effectivePrice(s, leadPrice);
    if (edge === "top") return price === null ? p !== null : p !== null && p > price;
    return p === null ? price !== null : price !== null && p < price;
  });
  const note = blocking.length
    ? `${blocking.length} campaign(s) at ${edge === "top" ? "a higher" : "a lower"} price stay ${
        edge === "top" ? "ahead" : "behind"
      } in this tier (price decides first).`
    : null;
  return { slots: replaceSlots(slots, updates), error: null, note };
};

const tierNumbers = (slots, exceptId, exitOffer) =>
  [...new Set(slots.filter((s) => s.campaignId !== exceptId && (exitOffer === undefined || (s.exitOffer === true) === exitOffer)).map((s) => num(s.tier)).filter((t) => t !== null))].sort(
    (a, b) => a - b
  );

/** Puts the campaign alone in a new first tier (shifting every tier down by one when tier 1 is taken). */
export const moveToTreeTop = (slots, campaignId) => {
  const slot = slots.find((s) => s.campaignId === campaignId);
  if (!slot) return { slots, error: "Campaign is not in this tree" };
  const tiers = tierNumbers(slots, campaignId, slot.exitOffer === true);
  if (tiers.length === 0) return { slots: replaceSlots(slots, { [campaignId]: { tier: 1 } }), error: null };
  if (tiers[0] > 1) return { slots: replaceSlots(slots, { [campaignId]: { tier: tiers[0] - 1 } }), error: null };
  if (tiers[tiers.length - 1] + 1 > MAX_TIER) return { slots, error: `Tiers would go above ${MAX_TIER}; renumber tiers first` };
  return {
    slots: slots.map((s) => {
      if ((s.exitOffer === true) !== (slot.exitOffer === true)) return s;
      if (s.campaignId === campaignId) return withTier(s, 1);
      const t = num(s.tier);
      return t === null ? s : withTier(s, t + 1);
    }),
    error: null,
  };
};

/** Puts the campaign alone in a new last tier (untiered campaigns still come after it). */
export const moveToTreeBottom = (slots, campaignId) => {
  const slot = slots.find((s) => s.campaignId === campaignId);
  if (!slot) return { slots, error: "Campaign is not in this tree" };
  const tiers = tierNumbers(slots, campaignId, slot.exitOffer === true);
  const next = tiers.length ? tiers[tiers.length - 1] + 1 : 1;
  if (next > MAX_TIER) return { slots, error: `Tiers would go above ${MAX_TIER}; renumber tiers first` };
  return { slots: replaceSlots(slots, { [campaignId]: { tier: next } }), error: null };
};

/** Moves a campaign to another tier (null = no tier, tried last). */
export const moveToTier = (slots, campaignId, tier) => {
  const t = toNumber(tier);
  const err = fieldError("tier", tier);
  if (err) return { slots, error: err };
  return { slots: replaceSlots(slots, { [campaignId]: { tier: t } }), error: null };
};

/** Swaps a whole tier with the tier above or below it. */
export const moveTier = (slots, tier, direction, exitOffer = false) => {
  const tiers = tierNumbers(slots, undefined, exitOffer);
  const i = tiers.indexOf(tier);
  const j = direction === "up" ? i - 1 : i + 1;
  if (i < 0 || j < 0 || j >= tiers.length) return { slots, error: `Tier is already ${direction === "up" ? "first" : "last"}` };
  const other = tiers[j];
  return {
    slots: slots.map((s) => {
      if ((s.exitOffer === true) !== exitOffer) return s;
      const t = num(s.tier);
      if (t === tier) return withTier(s, other);
      if (t === other) return withTier(s, tier);
      return s;
    }),
    error: null,
  };
};

/** Renumbers tiers 1..n keeping their order (untiered stays untiered). */
export const renumberTiers = (slots) => {
  const tiers = tierNumbers(slots);
  const map = new Map(tiers.map((t, i) => [t, i + 1]));
  return slots.map((s) => {
    const t = num(s.tier);
    return t === null || map.get(t) === t ? s : withTier(s, map.get(t));
  });
};

export const addCampaign = (slots, campaign, tier) => {
  const slot = toSlot(campaign);
  if (!slot.campaignId) return { slots, error: "Pick a campaign" };
  if (slots.some((s) => s.campaignId === slot.campaignId)) return { slots, error: "Campaign is already in this tree" };
  if (slots.length >= MAX_CAMPAIGNS) return { slots, error: `At most ${MAX_CAMPAIGNS} campaigns per tree` };
  // The picker's values may be older than the database: only what staff set here is sent (the tier, if given);
  // price and priority keep their stored values unless edited.
  slot.omit = ["price", "priority", "product", "weight"];
  if (!isBlank(tier)) {
    const err = fieldError("tier", tier);
    if (err) return { slots, error: err };
    slot.tier = toNumber(tier);
  } else {
    slot.omit.push("tier");
  }
  return { slots: [...slots, slot], error: null };
};

export const removeCampaign = (slots, campaignId) => slots.filter((s) => s.campaignId !== campaignId);

/** Inline edit of tier/price/priority; keeps what staff typed so validation can flag it. */
export const setField = (slots, campaignId, field, value) =>
  slots.map((s) =>
    s.campaignId === campaignId
      ? { ...s, [field]: isBlank(value) ? null : value, omit: (s.omit || []).filter((f) => f !== field) }
      : s
  );

/** {campaignId: {field: message}} for every invalid tier/price/priority. */
export const validateSlots = (slots) => {
  const errors = {};
  slots.forEach((s) => {
    ["tier", "price", "priority"].forEach((f) => {
      const e = fieldError(f, s[f]);
      if (e) errors[s.campaignId] = { ...(errors[s.campaignId] || {}), [f]: e };
    });
  });
  return errors;
};

/** Display filters; never changes the walk order. */
export const filterSlots = (campaigns, { hideInactive = false, buyerId = "", minPrice = "", maxPrice = "" } = {}) => {
  const min = num(minPrice);
  const max = num(maxPrice);
  return campaigns.filter((s) => {
    if (hideInactive && (!s.active || s.capped)) return false;
    if (buyerId && s.buyerId !== buyerId) return false;
    const p = num(s.price);
    if (min !== null && (p === null || p < min)) return false;
    if (max !== null && (p === null || p > max)) return false;
    return true;
  });
};

/** The import document for POST /tree/import. Campaigns are written in walk order (with no lead price). */
export const buildImportDocument = ({ tree, slots, manualOrder, baseRevision, dryRun }) => {
  const doc = {
    format: TREE_FORMAT,
    tree: {
      ...(tree?.treeUniqueId !== undefined && tree?.treeUniqueId !== null ? { treeUniqueId: tree.treeUniqueId } : {}),
      name: tree?.name,
      comment: tree?.comment,
      pingTreeType: tree?.pingTreeType,
      ...(typeof tree?.active === "boolean" ? { active: tree.active } : {}),
      manualOrder: !!manualOrder,
      ...presentFields(tree, TREE_SETTING_FIELDS),
    },
    campaigns: walkOrder(slots, { manualOrder }).map((s) => {
      const entry = { campaignId: s.campaignId, uniqueId: s.uniqueId };
      CAMPAIGN_FIELDS.forEach((f) => {
        if (!(s.omit || []).includes(f) && (hasOwn(s, f) || ["tier", "price", "priority"].includes(f))) entry[f] = f === "product" ? s[f] : toWire(s[f]);
      });
      return entry;
    }),
  };
  // Legacy split-detail reservations cannot be imported; retain the marker so the API rejects a loaded legacy tree.
  if (Array.isArray(tree?.tailReservations) && tree.tailReservations.length) doc.tailReservations = tree.tailReservations;
  if (baseRevision) doc.baseRevision = baseRevision;
  if (dryRun !== undefined) doc.dryRun = dryRun;
  return doc;
};

/**
 * Stable fingerprint of what Save would write; unequal fingerprints = unsaved changes. Values that are not numbers
 * (e.g. "abc") are kept as typed, so an invalid edit never looks like the saved blank value.
 */
export const fingerprint = ({ tree, slots, manualOrder }) =>
  JSON.stringify({
    doc: buildImportDocument({ tree, slots, manualOrder }),
    invalid: slots
      .flatMap((s) => ["tier", "price", "priority"].map((f) => [s.campaignId, f, s[f]]))
      .filter(([, , v]) => Number.isNaN(toNumber(v)))
      .map(([id, f, v]) => `${id}:${f}:${String(v)}`)
      .sort(),
  });

/** The export document as a file for download. */
export const exportFileName = (tree) =>
  `ping-tree-${tree?.treeUniqueId ?? "new"}-${String(tree?.name || "tree")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "")}.json`;

/**
 * Checks an uploaded tree file before it is sent to the API (which checks again, against the database).
 * Returns {doc, errors: [{path, message}]}.
 */
export const parseTreeFile = (text) => {
  let doc;
  try {
    doc = JSON.parse(text);
  } catch (e) {
    return { doc: null, errors: [{ path: "file", message: `Not valid JSON: ${e.message}` }] };
  }
  const errors = [];
  if (!doc || typeof doc !== "object" || Array.isArray(doc)) {
    return { doc: null, errors: [{ path: "file", message: "The file must hold one JSON object" }] };
  }
  // An export saved straight from the API response envelope: {data: {...}}.
  if (doc.data && typeof doc.data === "object" && !doc.format) doc = doc.data;
  if (doc.format !== TREE_FORMAT) errors.push({ path: "format", message: `format must be "${TREE_FORMAT}"` });
  if (Array.isArray(doc.tailReservations) && doc.tailReservations.length)
    errors.push({ path: "tailReservations", message: "merge legacy tree-details reservations into one configuration before importing" });
  const tree = doc.tree;
  if (!tree || typeof tree !== "object") {
    errors.push({ path: "tree", message: "tree is required" });
  } else if (tree.pingTreeType !== undefined && !["redirect", "silent"].includes(String(tree.pingTreeType).toLowerCase())) {
    // optional for an update (the API keeps the stored type); a new tree needs it (checked where trees are created)
    errors.push({ path: "tree.pingTreeType", message: "pingTreeType must be 'Redirect' or 'Silent'" });
  }
  if (!Array.isArray(doc.campaigns)) {
    errors.push({ path: "campaigns", message: "campaigns must be a list" });
    return { doc, errors };
  }
  if (doc.campaigns.length > MAX_CAMPAIGNS) {
    errors.push({ path: "campaigns", message: `at most ${MAX_CAMPAIGNS} campaigns per tree` });
  }
  const seen = new Map();
  doc.campaigns.forEach((c, i) => {
    const path = `campaigns[${i}]`;
    if (!c || typeof c !== "object" || Array.isArray(c)) {
      errors.push({ path, message: "each campaign must be an object" });
      return;
    }
    const key = !isBlank(c.campaignId) ? `id:${c.campaignId}` : !isBlank(c.uniqueId) ? `u:${c.uniqueId}` : null;
    if (!key) {
      errors.push({ path, message: "campaignId or uniqueId is required" });
      return;
    }
    if (seen.has(key)) {
      errors.push({ path, message: `listed more than once (also campaigns[${seen.get(key)}])` });
      return;
    }
    seen.set(key, i);
    ["tier", "price", "priority"].forEach((f) => {
      const e = fieldError(f, c[f]);
      if (e) errors.push({ path: `${path}.${f}`, message: e });
    });
  });
  return { doc, errors };
};

/**
 * Turns a parsed file's campaigns into editor slots. Names, buyer and status come from the known campaigns
 * (get-all-campaign rows) when listed there; values from the file win. A campaign named by campaignId that is not in
 * the known list is kept as the file describes it (the API checks it on Save); one named only by an unknown uniqueId
 * cannot be kept and is reported. Returns {slots, errors, unresolved}.
 */
export const slotsFromDocument = (doc, knownCampaigns) => {
  const byId = new Map();
  const byUnique = new Map();
  (knownCampaigns || []).forEach((c) => {
    const s = toSlot(c);
    if (s.campaignId) byId.set(String(s.campaignId), s);
    if (s.uniqueId !== null) byUnique.set(String(s.uniqueId), s);
  });
  const slots = [];
  const errors = [];
  let unresolved = 0;
  (doc?.campaigns || []).forEach((c, i) => {
    let known = !isBlank(c.campaignId) ? byId.get(String(c.campaignId)) : byUnique.get(String(c.uniqueId));
    if (known && !isBlank(c.campaignId) && !isBlank(c.uniqueId) && String(known.uniqueId) !== String(c.uniqueId)) {
      errors.push({ path: `campaigns[${i}].uniqueId`, message: `uniqueId ${c.uniqueId} does not match campaign ${c.campaignId} (${known.uniqueId})` });
      return;
    }
    if (!known) {
      if (isBlank(c.campaignId)) {
        errors.push({ path: `campaigns[${i}]`, message: `campaign with uniqueId ${c.uniqueId} not found; use its campaignId` });
        return;
      }
      known = toSlot(c);
      unresolved += 1;
    }
    const slot = { ...known };
    // Picker rows may lack export-only role hints; retain the file preview hint until the API checks current roles.
    ["exitOffer", "position"].forEach((field) => {
      if (!hasOwn(known, field) && hasOwn(c, field)) slot[field] = c[field];
    });
    // Fields the file leaves out keep their stored values on the API side: they stay omitted (not sent) until
    // staff edit them, so a value loaded earlier can never overwrite a newer one.
    slot.omit = CAMPAIGN_FIELDS.filter((f) => !Object.prototype.hasOwnProperty.call(c, f));
    CAMPAIGN_FIELDS.forEach((f) => {
      if (Object.prototype.hasOwnProperty.call(c, f)) slot[f] = isBlank(c[f]) ? null : c[f];
    });
    slots.push(slot);
  });
  return { slots, errors, unresolved };
};

/**
 * Maps API errors ({path: "campaigns[3].tier", message}) onto the campaigns of the document that was sent.
 * Returns {byCampaign: {campaignId: {field|row: message}}, general: [message]}.
 */
export const mapServerErrors = (errors, sentCampaigns) => {
  const byCampaign = {};
  const general = [];
  (errors || []).forEach((e) => {
    const m = /^campaigns\[(\d+)\](?:\.(\w+))?$/.exec(e?.path || "");
    const campaign = m ? sentCampaigns?.[Number(m[1])] : null;
    if (campaign?.campaignId) {
      const key = m[2] && ["tier", "price", "priority"].includes(m[2]) ? m[2] : "row";
      byCampaign[campaign.campaignId] = { ...(byCampaign[campaign.campaignId] || {}), [key]: e.message };
    } else {
      general.push(e?.path ? `${e.path}: ${e.message}` : e?.message);
    }
  });
  return { byCampaign, general };
};

/**
 * The tree builder is for LDS admins only (staff login CU-LS-M1-040): the API refuses /tree/import and
 * /tree/{id}/export to editors and viewers, because an import rewrites the price of every listed campaign in every tree
 * that lists it. The portal's role list comes from the login token exchange; "admin" (or "lds_admin").
 */
export const isLdsAdmin = (userInfo) =>
  Array.isArray(userInfo?.roles) && userInfo.roles.some((r) => ["admin", "lds_admin"].includes(String(r).toLowerCase()));

/** Text for a failed API call (RTK Query error): a 403 means the account lacks the LDS admin role. */
export const apiErrorText = (error, fallback) =>
  error?.status === 403
    ? "Your account does not have the LDS admin role, which the tree builder needs. Ask an LDS admin."
    : error?.status === 401
    ? "Your login has expired. Log in again."
    : fallback;

/** Distinct buyers in the tree, for the buyer filter. */
export const buyersOf = (slots) => {
  const map = new Map();
  slots.forEach((s) => {
    if (s.buyerId && !map.has(s.buyerId)) map.set(s.buyerId, s.buyerName || s.buyerId);
  });
  return [...map.entries()].map(([buyerId, buyerName]) => ({ buyerId, buyerName }));
};
